// Mercapoli - Contexto del Carrito de Compras
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize from localStorage directly
  const getInitialState = () => {
    if (typeof window !== 'undefined') {
      const savedCart = localStorage.getItem('mercapoli_cart');
      if (savedCart) {
        try {
          return JSON.parse(savedCart);
        } catch (e) {
          console.error('Error parsing cart:', e);
        }
      }
    }
    return { items: [], restauranteId: null, restauranteInfo: null };
  };

  const initialState = getInitialState();
  const [items, setItems] = useState(initialState.items);
  const [restauranteId, setRestauranteId] = useState(initialState.restauranteId);
  const [restauranteInfo, setRestauranteInfo] = useState(initialState.restauranteInfo);
  const [isInitialized, setIsInitialized] = useState(false);

  // Mark as initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Guardar carrito en localStorage (only after initialization)
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('mercapoli_cart', JSON.stringify({
        items,
        restauranteId,
        restauranteInfo
      }));
    }
  }, [items, restauranteId, restauranteInfo, isInitialized]);

  const addItem = useCallback((producto, cantidad = 1, notas = '') => {
    // Si el carrito tiene items de otro restaurante, preguntar si limpiar
    if (restauranteId && producto.restaurante_id !== restauranteId) {
      return { needsConfirmation: true, currentRestaurante: restauranteInfo?.nombre };
    }

    setRestauranteId(producto.restaurante_id);

    setItems(prevItems => {
      const existingIndex = prevItems.findIndex(
        item => item.producto_id === producto.producto_id
      );

      if (existingIndex >= 0) {
        // Actualizar cantidad
        const newItems = [...prevItems];
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          cantidad: newItems[existingIndex].cantidad + cantidad,
          notas: notas || newItems[existingIndex].notas
        };
        return newItems;
      } else {
        // Agregar nuevo item
        return [...prevItems, {
          producto_id: producto.producto_id,
          nombre: producto.nombre,
          precio: producto.precio,
          imagen: producto.imagen,
          cantidad,
          notas
        }];
      }
    });

    return { success: true };
  }, [restauranteId, restauranteInfo]);

  const removeItem = useCallback((productoId) => {
    setItems(prevItems => {
      const newItems = prevItems.filter(item => item.producto_id !== productoId);
      if (newItems.length === 0) {
        setRestauranteId(null);
        setRestauranteInfo(null);
      }
      return newItems;
    });
  }, []);

  const updateQuantity = useCallback((productoId, cantidad) => {
    if (cantidad <= 0) {
      removeItem(productoId);
      return;
    }

    setItems(prevItems =>
      prevItems.map(item =>
        item.producto_id === productoId
          ? { ...item, cantidad }
          : item
      )
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
    setRestauranteId(null);
    setRestauranteInfo(null);
  }, []);

  const setRestaurante = useCallback((restaurante) => {
    setRestauranteId(restaurante.restaurante_id);
    setRestauranteInfo({
      restaurante_id: restaurante.restaurante_id,
      nombre: restaurante.nombre,
      costo_envio: restaurante.costo_envio,
      tiempo_preparacion_min: restaurante.tiempo_preparacion_min
    });
  }, []);

  const forceAddItem = useCallback((producto, cantidad = 1, notas = '', restaurante = null) => {
    // Limpiar carrito y agregar nuevo item
    setItems([{
      producto_id: producto.producto_id,
      nombre: producto.nombre,
      precio: producto.precio,
      imagen: producto.imagen,
      cantidad,
      notas
    }]);
    setRestauranteId(producto.restaurante_id);
    if (restaurante) {
      setRestauranteInfo({
        restaurante_id: restaurante.restaurante_id,
        nombre: restaurante.nombre,
        costo_envio: restaurante.costo_envio,
        tiempo_preparacion_min: restaurante.tiempo_preparacion_min
      });
    }
  }, []);

  // Calcular totales
  const subtotal = items.reduce((sum, item) => sum + (item.precio * item.cantidad), 0);
  const costoEnvio = restauranteInfo?.costo_envio || 0;
  const total = subtotal + costoEnvio;
  const itemCount = items.reduce((sum, item) => sum + item.cantidad, 0);

  const value = {
    items,
    restauranteId,
    restauranteInfo,
    subtotal,
    costoEnvio,
    total,
    itemCount,
    isEmpty: items.length === 0,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setRestaurante,
    forceAddItem
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
